import { toInteger } from 'lodash';
import React, { useContext, useMemo, useState } from 'react';
import { NodeProps } from 'reactflow';
import styled from 'styled-components/macro';
import { assertNotNull } from '../../../../../helpers/nullHelpers';
import { ReactComponent as Water } from '../../../../../images/icons/water-solid.svg';
import { StyledLabel } from '../../../../../shared/form/FieldLabel';
import { getNullSelectOption, Select, SelectOption } from '../../../../../shared/form/inputs/Select';
import { TextInput } from '../../../../../shared/TextInput';
import { PipeFormModel } from '../../GetDataForQuoteOrDesignForm';
import { QuoteOrDesignFormContext } from '../../QuoteOrDesignFormContext';
import { UnitFieldContext } from '../../units/UnitFieldContext';
import { DeletePopover } from '../DeletePopover';
import { useTree } from '../map-helpers/hooks/useTree';
import { getFlowRateString, getPressureDropString } from '../map-helpers/mapHelpers';
import { PipeMapNodeData } from '../mapNode';
import { NukePopover } from '../NukePopover';
import { PlusPopover } from '../PlusPopover';
import { PipeFields, TreeNode, TreeNodeType } from '../treeNode';
import { MapFlowNode } from './MapFlowNode';

export const Pipe = ({
  data: {
    parentTreeNode,
    isFirstChild,
    flowRateInLitresPerMinute,
    velocityInMetresPerSecond,
    pressureDropInKilopascals,
    state,
    markedForDeletion,
  },
}: NodeProps<PipeMapNodeData>) => {
  const { referenceData } = assertNotNull(useContext(QuoteOrDesignFormContext));
  const { tree, replaceNode, addLeaf } = useTree();
  const { unitIndex } = assertNotNull(useContext(UnitFieldContext));
  const [currentPipeFields, setCurrentPipeFields] = useState<PipeFields | null>(null);
  const [childNode, setChildNode] = useState<TreeNode | null>(null);
  const [currentPipe, setCurrentPipe] = useState<PipeFormModel | null>(null);
  const [pipeOptions, setPipeOptions] = useState<Array<SelectOption<PipeFormModel | null>>>([]);

  useMemo(() => {
    setPipeOptions(referenceData.pipeOptions.filter(pipe => !pipe.value?.isUnderfloorHeating));
    setCurrentPipeFields(
      parentTreeNode.nodeType === TreeNodeType.Junction && !isFirstChild
        ? parentTreeNode.secondChild.exitPipe
        : parentTreeNode.firstChild.exitPipe,
    );
  }, [parentTreeNode, isFirstChild, referenceData.pipeOptions]);

  useMemo(() => {
    if (pipeOptions && currentPipeFields) {
      setCurrentPipe(
        pipeOptions.find(pipe => pipe.value?.code === currentPipeFields?.code)?.value ??
          getNullSelectOption<PipeFormModel>().value,
      );
    }
  }, [pipeOptions, currentPipeFields]);

  useMemo(() => {
    const childId =
      parentTreeNode.nodeType === TreeNodeType.Junction && !isFirstChild
        ? parentTreeNode.secondChild.id
        : parentTreeNode.firstChild.id;
    if (childId !== null && tree[childId]) {
      setChildNode(tree[childId]);
    } else if (childId === null) {
      setChildNode(null);
    }
  }, [isFirstChild, parentTreeNode, tree]);

  if (!parentTreeNode) {
    return null;
  }

  const onChangePipe = (newPipe: PipeFormModel | null) => {
    const newPipeFields = {
      code: newPipe?.code ?? null,
      lengthInMetres: currentPipeFields?.lengthInMetres ?? null,
      markedForDeletion: currentPipeFields?.markedForDeletion ?? false,
    };
    replacePipeNode(newPipeFields);
  };

  const onChangeWrapper = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    onChangeLength(newValue);
  };

  const onChangeLength = (newLength: string) => {
    const newPipeFields = {
      code: currentPipeFields?.code ?? null,
      lengthInMetres: parseFloat(newLength) ?? null,
      markedForDeletion: currentPipeFields?.markedForDeletion ?? false,
    };
    replacePipeNode(newPipeFields);
  };

  const replacePipeNode = (newPipeFields: PipeFields) => {
    if (parentTreeNode.nodeType === TreeNodeType.Junction && !isFirstChild) {
      const newNode: TreeNode = {
        ...parentTreeNode,
        secondChild: {
          ...parentTreeNode.secondChild,
          exitPipe: newPipeFields,
        },
      };
      replaceNode(parentTreeNode.id, newNode);
    } else {
      const newNode: TreeNode = {
        ...parentTreeNode,
        firstChild: {
          ...parentTreeNode.firstChild,
          exitPipe: newPipeFields,
        },
      };
      replaceNode(parentTreeNode.id, newNode);
    }
  };

  const selectFieldName = 'pipeType' + parentTreeNode.id + toInteger(isFirstChild);
  const textFieldName = 'pipeLength' + parentTreeNode.id + toInteger(isFirstChild);

  const flowRate = getFlowRateString(flowRateInLitresPerMinute, velocityInMetresPerSecond);
  const pressureDrop = getPressureDropString(pressureDropInKilopascals);

  const addChild = (nodeType: TreeNodeType) => {
    addLeaf(nodeType, parentTreeNode.id, isFirstChild);
  };

  const getChildId = (treeNode: TreeNode) => {
    if (treeNode.nodeType === TreeNodeType.Manifold) {
      return null;
    }

    return treeNode.nodeType === TreeNodeType.Junction && !isFirstChild
      ? treeNode.secondChild.id
      : treeNode.firstChild.id;
  };

  return (
    <MapFlowNode
      icon={<PipeIcon />}
      stats={[flowRate, pressureDrop]}
      hasSource={true}
      hasTarget={true}
      title="Pipe"
      state={state}
      markedForDeletion={markedForDeletion}
      popoverButtons={
        <>
          {childNode && childNode.nodeType === TreeNodeType.Junction && <NukePopover nodeId={childNode.id} />}
          {parentTreeNode.nodeType === TreeNodeType.Junction && (
            <DeletePopover node={parentTreeNode} replaceWithFirstChild={!isFirstChild} />
          )}
          <PlusPopover
            testId={pipePlusButtonTestId(unitIndex, parentTreeNode.id, isFirstChild)}
            addChildOfNodeType={addChild}
            showAddManifold={!Boolean(getChildId(parentTreeNode))}
          />
        </>
      }
    >
      <Select name={selectFieldName} value={currentPipe} options={pipeOptions} isSmall={true} onChange={onChangePipe} />
      <StyledLabel htmlFor={textFieldName}>Length (m)</StyledLabel>
      <TextInput
        name={textFieldName}
        $isSmall={true}
        onChange={onChangeWrapper}
        value={currentPipeFields?.lengthInMetres?.toString() ?? ''}
      />
    </MapFlowNode>
  );
};

const PipeIcon = styled(Water)`
  height: 1em;
  width: 1em;
`;

export const pipePlusButtonTestId = (unitIndex: number, parentId: string, isFirstChild: boolean) =>
  `unit-${unitIndex}-pipe-plus-button-${parentId}-${isFirstChild ? 'first-child' : 'second-child'}`;
